import { ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

export interface UseSearchParamsStateOptions {
  parseArrays?: boolean;
}
/**
 * A Vue Composition API hook to manage and synchronize state with URL search parameters.
 *
 * @param {T} INITIAL_STATE - The initial state object.
 * @param {UseSearchParamsStateOptions} [options] - Optional configuration options.
 * @returns {Ref<T>} - A reactive reference to the state object.
 */
export default function useSearchParamsState<
  T extends Record<string, any>
>(INITIAL_STATE: T, options?: UseSearchParamsStateOptions) {
  const route = useRoute();
  const router = useRouter();

  const updateSearchParams = (params: Record<string, boolean | number | string>) => {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
        searchParams.delete(key);
        return;
      }
      searchParams.set(key, String(value));
    });

    router.replace({ query: Object.fromEntries(searchParams) });
  };

  const parseSearchToState = (seachState: Record<string, any>) => {
    let parsedState = {
      ...INITIAL_STATE,
      ...Object.fromEntries(new URLSearchParams(seachState as Record<string, string>)),
    };

    // Convert comma-separated strings back to arrays for specific keys
    if (options?.parseArrays) {
      Object.keys(INITIAL_STATE).forEach((key) => {
        if (Array.isArray(INITIAL_STATE[key]) && typeof parsedState[key] === 'string') {
          parsedState = {
            ...parsedState,
            [key]: (parsedState[key] as string).split(','),
          };
        }
      });
    }

    return parsedState;
  };

  // Initialize state with default values or current search params
  const state = ref<T>({
    ...INITIAL_STATE,
    ...parseSearchToState(route.query),
  });

  // sync search params with INITIAL_STATE
  updateSearchParams(state.value);

  // Watch for changes in the state and sync with browser search params
  watch(
    state,
    (newState) => {
      updateSearchParams(newState);
    },
    { deep: true },
  );

  // Watch for changes in the route query and update the state
  watch(
    () => route.query,
    (newQuery) => {
      state.value = parseSearchToState(newQuery);
    },
  );

  return state;
}
